import { ApiQueryName } from "@/models/api"
import { Site } from "@/models/site"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import type { SpraypaintQueryOptions, UseApiOptions } from "../types"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

const placeholderSites: Site[] = Array.from(
  { length: 3 },
  (_, i) => new Site({ id: String(i + 1), name: "" })
)

/**
 * Gets sites
 */
const getSites = async ({
  extraFields,
  includes,
  page,
  pageSize,
  sort,
  where,
}: SpraypaintQueryOptions): Promise<Site[]> => {
  try {
    const response: CollectionProxy<Site> = await Site.where(where)
      .includes(includes ?? [])
      .order(sort ?? { name: "asc" })
      .page(page ?? 1)
      .per(pageSize ?? 250)
      .selectExtra(extraFields ?? [])
      .all()

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Returns sites
 *
 * @example
 * const {
 *   sitesData,
 *   sitesIsPending,
 *   sitesError
 *  } = useSites(options)
 */
export const useSites = (
  options: UseApiOptions<SpraypaintQueryOptions, Site[], HttpError> = {}
) => {
  const { apiQueryOptions = {}, queryOptions = {} } = options

  const queryKey = createQueryKey(ApiQueryName.Sites, "getMany", null, {
    apiQueryOptions,
  })
  const queryResult = useQuery({
    placeholderData: placeholderSites,
    ...queryOptions,
    queryFn: () => getSites(apiQueryOptions),
    queryKey,
    throwOnError: resolveQueryThrowOnError(queryOptions.throwOnError),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Sites)
}
